import React, { createContext, useCallback, useContext, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import DsRoot from 'src/component/ds/DsRoot';
import useDsScreen from 'src/component/ds/useDsScreen';
import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle } from 'src/component/ds/ui/sheet';

interface DsPanelSlotValue {
  slot: HTMLElement | null;
  setSlot: (el: HTMLElement | null) => void;
  /** Makes this the open panel, closing the one before it. Returns the release. */
  claim: (close: () => void) => () => void;
}

const DsPanelSlotContext = createContext<DsPanelSlotValue | null>(null);

function useDsPanelSlot () {
  const ctx = useContext(DsPanelSlotContext);
  if (!ctx) {
    throw new Error('DsPanel must be used inside a v2 screen (DsPanelProvider)');
  }
  return ctx;
}

/** Mounted by ProtectedRoute for v2 routes only. */
export function DsPanelProvider ({ children }: { children: React.ReactNode }) {
  const [slot, setSlot] = useState<HTMLElement | null>(null);

  // One panel at a time: each screen keeps its own open state, so opening one closes the last.
  const closeOpenPanel = useRef<(() => void) | null>(null);
  const claim = useCallback((close: () => void) => {
    const previous = closeOpenPanel.current;
    closeOpenPanel.current = close;
    previous?.();
    return () => {
      if (closeOpenPanel.current === close) {
        closeOpenPanel.current = null;
      }
    };
  }, []);

  const value = useMemo(() => ({ slot, setSlot, claim }), [slot, claim]);

  return <DsPanelSlotContext.Provider value={value}>{children}</DsPanelSlotContext.Provider>;
}

/** Where a docked panel lands, beside the content. `contents` makes the panel itself the flex item. */
export function DsPanelHost () {
  const { setSlot } = useDsPanelSlot();

  return <div ref={setSlot} className='ds-contents' />;
}

export interface DsPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: React.ReactNode;
  side?: 'left' | 'right';
  children: React.ReactNode;
  footer?: React.ReactNode;
}

/**
 * Side panel, rendered from the screen's own JSX so its children keep the
 * screen's props and providers. Docked beside the content on wide screens,
 * floating over it below ds-pair.
 */
export function DsPanel ({ open, ...props }: DsPanelProps) {
  if (!open) {
    return null;
  }

  return <OpenPanel {...props} />;
}

/* Split out so the breakpoint listener only exists while a panel is open. */
function OpenPanel ({ onOpenChange, title, side = 'right', children, footer }: Omit<DsPanelProps, 'open'>) {
  const { slot, claim } = useDsPanelSlot();
  const docked = useDsScreen('ds-pair');

  const onOpenChangeRef = useRef(onOpenChange);
  onOpenChangeRef.current = onOpenChange;
  useLayoutEffect(() => claim(() => onOpenChangeRef.current(false)), [claim]);

  const sheet = (
    <Sheet open onOpenChange={onOpenChange}>
      <SheetContent side={side} docked={docked} aria-describedby={undefined} className='ds-gap-0'>
        <SheetHeader>
          <SheetTitle>{title}</SheetTitle>
        </SheetHeader>
        <div className='ds-min-h-0 ds-flex-1 ds-overflow-y-auto ds-px-4 ds-py-2'>{children}</div>
        {footer && (
          <SheetFooter className='ds-flex-row ds-justify-end ds-border-t ds-border-border'>{footer}</SheetFooter>
        )}
      </SheetContent>
    </Sheet>
  );

  if (!docked) {
    return sheet;
  }

  if (!slot) {
    return null;
  }

  return createPortal(
    <DsRoot className={side === 'left' ? 'ds-order-first ds-h-full' : 'ds-h-full'}>{sheet}</DsRoot>,
    slot
  );
}
